import { collectEvent, UserAction, userActionTypeEnum, } from '@thoughtspot/metrics';
import { EMAIL_CUSTOMIZATION_CONSTANTS } from './constants';
export const trackEmailCustomisationOpened = (hasCustomisation, customisationID, renderTime) => {
    const userAction = new UserAction(userActionTypeEnum.EMAIL_CUSTOMISATION_OPENED);
    collectEvent(userAction, {
        timestamp: Date.now(),
        hasCustomisation,
        customisationID: hasCustomisation ? customisationID : 0,
        renderTime,
    });
};
export const trackEmailCustomisationEditTriggered = (hasCustomisation, customisationID) => {
    const userAction = new UserAction(userActionTypeEnum.EMAIL_CUSTOMISATION_EDIT_TRIGGERED);
    collectEvent(userAction, {
        timestamp: Date.now(),
        hasCustomisation,
        customisationID: hasCustomisation ? customisationID : 0,
    });
};
export const DEFAULT_SETTINGS = {
    logo: EMAIL_CUSTOMIZATION_CONSTANTS.SAME_AS_STYLING,
    logoSize: 'Medium',
    buttonColor: '#000000',
    primaryTextColor: '#ffffff',
    cardBackgroundColor: '#f4f4f4',
    mobileAppNudge: EMAIL_CUSTOMIZATION_CONSTANTS.VISIBILITY_SHOWN,
    hideModifyAlert: EMAIL_CUSTOMIZATION_CONSTANTS.VISIBILITY_SHOWN,
    hideUnsubscribeLink: EMAIL_CUSTOMIZATION_CONSTANTS.VISIBILITY_SHOWN,
    hideManageNotification: EMAIL_CUSTOMIZATION_CONSTANTS.VISIBILITY_SHOWN,
    companySignature: '',
    webLinkLabel1: 'Privacy Policy',
    webLinkAddress1: 'https://www.thoughtspot.com/privacy-statement',
    webLinkLabel2: 'Contact Support',
    contactSupportUrl: 'https://www.thoughtspot.com/support',
    companyWebsiteUrl: 'https://www.thoughtspot.com',
    productName: 'ThoughtSpot',
    footerAddress: '444 Castro St, Suite 1000 Mountain View, CA 94041',
    mobileNumber: '(800) 508-7008',
    hideProductName: EMAIL_CUSTOMIZATION_CONSTANTS.VISIBILITY_SHOWN,
    hideFooterAddress: EMAIL_CUSTOMIZATION_CONSTANTS.VISIBILITY_SHOWN,
    hideFooterPhone: EMAIL_CUSTOMIZATION_CONSTANTS.VISIBILITY_SHOWN,
    hideNotificationStatus: EMAIL_CUSTOMIZATION_CONSTANTS.VISIBILITY_SHOWN,
    hideErrorMessage: EMAIL_CUSTOMIZATION_CONSTANTS.VISIBILITY_SHOWN,
    hideContactSupportUrl: EMAIL_CUSTOMIZATION_CONSTANTS.VISIBILITY_SHOWN,
    hidePrivacyPolicy: EMAIL_CUSTOMIZATION_CONSTANTS.VISIBILITY_SHOWN,
    hideLogoUrl: EMAIL_CUSTOMIZATION_CONSTANTS.VISIBILITY_SHOWN,
    replacementValueForAnswer: 'Answer',
    replacementValueForLiveboard: 'Liveboard',
    replacementValueForSpotIQ: 'SpotIQ',
    hide_ts_vocabulary_definitions: false,
};
const isPropertyUpdated = (oldValue, newValue, defaultValue) => {
    if (newValue === defaultValue) {
        return 'default';
    }
    if (oldValue !== newValue) {
        return 'true';
    }
    return 'false';
};
const isVisibilityPropertyUpdated = (oldValue, newValue) => {
    return isPropertyUpdated(oldValue, newValue, EMAIL_CUSTOMIZATION_CONSTANTS.VISIBILITY_SHOWN);
};
export const calculateSaveEventProperties = (oldSettings, newSettings, oldCustomisationID, newCustomisationID, duration, isConfigurationReset) => {
    let totalPropertiesUpdated = 0;
    const isLogoUpdated = isPropertyUpdated(oldSettings.logo, newSettings.logo, DEFAULT_SETTINGS.logo);
    if (isLogoUpdated === 'true')
        totalPropertiesUpdated++;
    const isLogoVisible = isVisibilityPropertyUpdated(oldSettings.hideLogoUrl, newSettings.hideLogoUrl);
    if (isLogoVisible === 'true')
        totalPropertiesUpdated++;
    const isPrimaryBackgroundUpdated = isPropertyUpdated(oldSettings.cardBackgroundColor, newSettings.cardBackgroundColor, DEFAULT_SETTINGS.cardBackgroundColor);
    if (isPrimaryBackgroundUpdated === 'true')
        totalPropertiesUpdated++;
    const isCTAButtonColorUpdated = isPropertyUpdated(oldSettings.buttonColor, newSettings.buttonColor, DEFAULT_SETTINGS.buttonColor);
    if (isCTAButtonColorUpdated === 'true')
        totalPropertiesUpdated++;
    const isCTAButtonTextUpdated = isPropertyUpdated(oldSettings.primaryTextColor, newSettings.primaryTextColor, DEFAULT_SETTINGS.primaryTextColor);
    if (isCTAButtonTextUpdated === 'true')
        totalPropertiesUpdated++;
    const isMobileAppNudgeVisible = isVisibilityPropertyUpdated(oldSettings.mobileAppNudge, newSettings.mobileAppNudge);
    if (isMobileAppNudgeVisible === 'true')
        totalPropertiesUpdated++;
    const isModifyAlertsVisible = isVisibilityPropertyUpdated(oldSettings.hideModifyAlert, newSettings.hideModifyAlert);
    if (isModifyAlertsVisible === 'true')
        totalPropertiesUpdated++;
    const isUnsubscribeVisible = isVisibilityPropertyUpdated(oldSettings.hideUnsubscribeLink, newSettings.hideUnsubscribeLink);
    if (isUnsubscribeVisible === 'true')
        totalPropertiesUpdated++;
    const isProductDefinitionVisible = isPropertyUpdated(oldSettings.hide_ts_vocabulary_definitions, newSettings.hide_ts_vocabulary_definitions, DEFAULT_SETTINGS.hide_ts_vocabulary_definitions);
    if (isProductDefinitionVisible === 'true')
        totalPropertiesUpdated++;
    const isProductNameVisible = isVisibilityPropertyUpdated(oldSettings.hideProductName, newSettings.hideProductName);
    if (isProductNameVisible === 'true')
        totalPropertiesUpdated++;
    const isProductNameUpdated = isPropertyUpdated(oldSettings.productName, newSettings.productName, DEFAULT_SETTINGS.productName);
    if (isProductNameUpdated === 'true')
        totalPropertiesUpdated++;
    const isCompanyWebsiteURLUpdated = isPropertyUpdated(oldSettings.companyWebsiteUrl, newSettings.companyWebsiteUrl, DEFAULT_SETTINGS.companyWebsiteUrl);
    if (isCompanyWebsiteURLUpdated === 'true')
        totalPropertiesUpdated++;
    const isFooterPhoneVisible = isVisibilityPropertyUpdated(oldSettings.hideFooterPhone, newSettings.hideFooterPhone);
    if (isFooterPhoneVisible === 'true')
        totalPropertiesUpdated++;
    const isFooterPhoneUpdated = isPropertyUpdated(oldSettings.mobileNumber, newSettings.mobileNumber, DEFAULT_SETTINGS.mobileNumber);
    if (isFooterPhoneUpdated === 'true')
        totalPropertiesUpdated++;
    const isFooterAddressVisible = isVisibilityPropertyUpdated(oldSettings.hideFooterAddress, newSettings.hideFooterAddress);
    if (isFooterAddressVisible === 'true')
        totalPropertiesUpdated++;
    const isFooterAddressUpdated = isPropertyUpdated(oldSettings.footerAddress, newSettings.footerAddress, DEFAULT_SETTINGS.footerAddress);
    if (isFooterAddressUpdated === 'true')
        totalPropertiesUpdated++;
    const isPrivacyPolicyVisible = isVisibilityPropertyUpdated(oldSettings.hidePrivacyPolicy, newSettings.hidePrivacyPolicy);
    if (isPrivacyPolicyVisible === 'true')
        totalPropertiesUpdated++;
    const isErrorMessageVisible = isVisibilityPropertyUpdated(oldSettings.hideErrorMessage, newSettings.hideErrorMessage);
    if (isErrorMessageVisible === 'true')
        totalPropertiesUpdated++;
    const isPrivacyPolicyUpdated = isPropertyUpdated(oldSettings.webLinkAddress1, newSettings.webLinkAddress1, DEFAULT_SETTINGS.webLinkAddress1);
    if (isPrivacyPolicyUpdated === 'true')
        totalPropertiesUpdated++;
    const isLiveboardVocabularyUpdated = isPropertyUpdated(oldSettings.replacementValueForLiveboard, newSettings.replacementValueForLiveboard, DEFAULT_SETTINGS.replacementValueForLiveboard);
    if (isLiveboardVocabularyUpdated === 'true')
        totalPropertiesUpdated++;
    const isAnswerVocabularyUpdated = isPropertyUpdated(oldSettings.replacementValueForAnswer, newSettings.replacementValueForAnswer, DEFAULT_SETTINGS.replacementValueForAnswer);
    if (isAnswerVocabularyUpdated === 'true')
        totalPropertiesUpdated++;
    const isSpotIQVocabularyUpdated = isPropertyUpdated(oldSettings.replacementValueForSpotIQ, newSettings.replacementValueForSpotIQ, DEFAULT_SETTINGS.replacementValueForSpotIQ);
    if (isSpotIQVocabularyUpdated === 'true')
        totalPropertiesUpdated++;
    return {
        newCustomisationID: newCustomisationID || 0,
        oldCustomisationID: oldCustomisationID || 0,
        totalPropertiesUpdated,
        duration,
        isLogoUpdated,
        isLogoVisible,
        isPrimaryBackgroundUpdated,
        isCTAButtonColorUpdated,
        isCTAButtonTextUpdated,
        isMobileAppNudgeVisible,
        isModifyAlertsVisible,
        isUnsubscribeVisible,
        isProductDefinitionVisible,
        isProductNameVisible,
        isProductNameUpdated,
        isCompanyWebsiteURLUpdated,
        isFooterPhoneVisible,
        isFooterPhoneUpdated,
        isFooterAddressVisible,
        isFooterAddressUpdated,
        isPrivacyPolicyVisible,
        isPrivacyPolicyUpdated,
        isErrorMessageVisible,
        isLiveboardVocabularyUpdated,
        isAnswerVocabularyUpdated,
        isSpotIQVocabularyUpdated,
        isConfigurationReset,
    };
};
export const trackEmailCustomisationEditSaved = (oldSettings, newSettings, oldCustomisationID, newCustomisationID, duration, isConfigurationReset) => {
    const userAction = new UserAction(userActionTypeEnum.EMAIL_CUSTOMISATION_EDIT_SAVED);
    const properties = calculateSaveEventProperties(oldSettings, newSettings, oldCustomisationID, newCustomisationID, duration, isConfigurationReset);
    collectEvent(userAction, {
        timestamp: Date.now(),
        ...properties,
    });
};
export const trackEmailCustomisationSaveFailed = (oldSettings, newSettings, oldCustomisationID, newCustomisationID, duration, isConfigurationReset, errorCode) => {
    const userAction = new UserAction(userActionTypeEnum.EMAIL_CUSTOMISATION_SAVE_FAILED);
    const properties = calculateSaveEventProperties(oldSettings, newSettings, oldCustomisationID, newCustomisationID, duration, isConfigurationReset);
    collectEvent(userAction, {
        timestamp: Date.now(),
        ...properties,
        errorCode,
    });
};
export const hasUnsavedChanges = (initialSettings, currentSettings) => {
    return JSON.stringify(initialSettings) !== JSON.stringify(currentSettings);
};
export const trackEmailCustomisationCancelled = (oldSettings, newSettings, oldCustomisationID, newCustomisationID, duration, isConfigurationReset) => {
    const userAction = new UserAction(userActionTypeEnum.EMAIL_CUSTOMISATION_CANCELLED);
    const properties = calculateSaveEventProperties(oldSettings, newSettings, oldCustomisationID, newCustomisationID, duration, isConfigurationReset);
    const hasUnsaved = hasUnsavedChanges(oldSettings, newSettings);
    collectEvent(userAction, {
        timestamp: Date.now(),
        ...properties,
        hasUnsavedChanges: hasUnsaved,
    });
};
//# sourceMappingURL=email-customization-analytics.js.map